import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  Divider,
  Slider,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplayIcon from '@mui/icons-material/Replay';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { SETUP_COPY, type SetupCopy } from './setupCopy.js';
import {
  planInstall,
  availableProvisioners,
  DEFAULT_ANSWERS,
  type Answers,
  type Cloud,
  type Hosting,
  type Provisioner,
  type Runtime,
} from './setupPlan.js';
import type { Lang } from './landingCopy.js';
import { DocsShell } from './DocsPage.js';

const EMERALD = '#2ec2b3';
const INK = '#0a0f16';

/** Volume choices people can actually place themselves on. */
/**
 * Monthly volumes the slider can land on.
 *
 * The middle of the range is where real installs sit, and the earlier jump
 * from 1M straight to 5M made the most common question — "50 thousand sessions
 * a day" — unanswerable: it is 1.5M, and there was no step for it.
 */
const SESSION_STEPS = [
  5_000, 25_000, 100_000, 250_000, 500_000, 1_000_000, 1_500_000, 3_000_000, 5_000_000, 10_000_000,
];
/** Only these get a printed label; the rest are unlabelled ticks. */
const SESSION_LABELS = new Set([5_000, 100_000, 500_000, 1_500_000, 5_000_000, 10_000_000]);
const RETENTION_STEPS = [7, 30, 90, 180, 365];

/**
 * A guided installation planner.
 *
 * The questions are the ones a pre-sales call asks anyway — where it runs,
 * on what, how it is provisioned, how much traffic. The value is in the last
 * screen: the commands, a configuration file carrying the sizing decisions,
 * and the handful of things that are otherwise found the hard way.
 *
 * Nothing here invents a capability. The provisioning step only offers what is
 * actually in the repository, so a customer is never pointed at a Terraform
 * module for a cloud that has none.
 */
export function SetupWizardPage({ lang = 'en' }: { lang?: Lang }) {
  const c = SETUP_COPY[lang];
  const [step, setStep] = useState(0);
  const [a, setA] = useState<Answers>(DEFAULT_ANSWERS);

  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setA((p) => ({ ...p, [k]: v }));

  // Changing an earlier answer can invalidate the provisioner chosen later;
  // rather than showing an impossible combination, snap to the first valid one.
  const provisioners = availableProvisioners(a);
  const effective: Answers = provisioners.includes(a.provisioner)
    ? a
    : { ...a, provisioner: provisioners[0] ?? 'compose' };

  const plan = useMemo(() => planInstall(effective), [effective]);
  const managed = a.hosting === 'managed';
  // Managed cloud has nothing to provision and nothing to size.
  const steps = managed ? 1 : 4;
  const atResult = step >= steps;

  const docsPrefix = lang === 'tr' ? '/tr/docs' : '/docs';

  return (
    <DocsShell lang={lang}>
      <Box sx={{ maxWidth: 880, mx: 'auto', px: 2, py: { xs: 4, md: 6 } }}>
      <Chip
        label={c.eyebrow}
        size="small"
        sx={{ mb: 2, bgcolor: 'rgba(46,194,179,0.12)', color: '#1a8f83', fontWeight: 600 }}
      />
      <Typography sx={{ fontWeight: 800, fontSize: { xs: 28, md: 38 }, letterSpacing: -1, lineHeight: 1.15, color: INK }}>
        {c.title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mt: 1.5, mb: 3, maxWidth: 680 }}>{c.subtitle}</Typography>

      {!atResult && (
        <>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {c.step(step + 1, steps)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={((step + 1) / steps) * 100}
              sx={{ flex: 1, height: 6, borderRadius: 3 }}
            />
          </Stack>

          <Paper sx={{ p: { xs: 2.5, md: 3.5 }, border: '1px solid', borderColor: 'divider' }}>
            {step === 0 && (
              <Question title={c.q1.title}>
                {(['managed', 'self', 'private', 'airgapped'] as Hosting[]).map((k) => (
                  <Choice
                    key={k}
                    selected={a.hosting === k}
                    label={c.q1[k].label}
                    hint={c.q1[k].hint}
                    onClick={() => set('hosting', k)}
                  />
                ))}
              </Question>
            )}

            {step === 1 && (
              <Stack spacing={3}>
                {a.hosting === 'private' && (
                  <Question title={c.q2.cloudTitle}>
                    {(['aws', 'azure', 'gcp', 'vmware', 'other'] as Cloud[]).map((k) => (
                      <Choice key={k} compact selected={a.cloud === k} label={c.q2[k]} onClick={() => set('cloud', k)} />
                    ))}
                  </Question>
                )}
                <Question title={c.q2.runtimeTitle}>
                  {(['compose', 'kubernetes', 'openshift'] as Runtime[]).map((k) => (
                    <Choice
                      key={k}
                      selected={a.runtime === k}
                      label={c.q2[k].label}
                      hint={c.q2[k].hint}
                      onClick={() => set('runtime', k)}
                    />
                  ))}
                </Question>
              </Stack>
            )}

            {step === 2 && (
              <Question title={c.q3.title} hint={c.q3.hint}>
                {provisioners.map((k: Provisioner) => (
                  <Choice
                    key={k}
                    selected={effective.provisioner === k}
                    label={c.q3[k].label}
                    hint={c.q3[k].hint}
                    onClick={() => set('provisioner', k)}
                  />
                ))}
              </Question>
            )}

            {step === 3 && (
              <Stack spacing={3.5}>
                <Typography variant="h6" fontWeight={700}>
                  {c.q4.title}
                </Typography>

                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {c.q4.sessions}: <b>{a.sessionsPerMonth.toLocaleString()}</b>
                    {c.result.perMonth}
                    {' · '}
                    <b>{Math.round(a.sessionsPerMonth / 30).toLocaleString()}</b>
                    {c.result.perDay}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.q4.sessionsHint}
                  </Typography>
                  <Slider
                    sx={{ mt: 1 }}
                    value={SESSION_STEPS.indexOf(a.sessionsPerMonth) === -1 ? 2 : SESSION_STEPS.indexOf(a.sessionsPerMonth)}
                    min={0}
                    max={SESSION_STEPS.length - 1}
                    step={1}
                    marks={SESSION_STEPS.map((v, i) => ({
                      value: i,
                      label: SESSION_LABELS.has(v)
                        ? v >= 1_000_000
                          ? `${v / 1_000_000}M`
                          : `${v / 1000}k`
                        : undefined,
                    }))}
                    onChange={(_, v) => set('sessionsPerMonth', SESSION_STEPS[v as number])}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {c.q4.retention}: <b>{a.retentionDays}</b>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.q4.retentionHint}
                  </Typography>
                  <Slider
                    sx={{ mt: 1 }}
                    value={RETENTION_STEPS.indexOf(a.retentionDays) === -1 ? 2 : RETENTION_STEPS.indexOf(a.retentionDays)}
                    min={0}
                    max={RETENTION_STEPS.length - 1}
                    step={1}
                    marks={RETENTION_STEPS.map((v, i) => ({ value: i, label: String(v) }))}
                    onChange={(_, v) => set('retentionDays', RETENTION_STEPS[v as number])}
                  />
                </Box>

                <Divider />

                <Box>
                  <FormControlLabel
                    control={<Switch checked={a.piiMasking} onChange={(e) => set('piiMasking', e.target.checked)} />}
                    label={<Typography variant="body2" fontWeight={600}>{c.q4.pii}</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                    {c.q4.piiHint}
                  </Typography>
                </Box>

                <Box>
                  <FormControlLabel
                    control={
                      <Switch checked={a.managedDatabase} onChange={(e) => set('managedDatabase', e.target.checked)} />
                    }
                    label={<Typography variant="body2" fontWeight={600}>{c.q4.managedDb}</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 6, mt: -0.5 }}>
                    {c.q4.managedDbHint}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Paper>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              disabled={step === 0}
              onClick={() => setStep((s) => s - 1)}
              sx={{ color: 'text.secondary' }}
            >
              {c.back}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setStep((s) => s + 1)}
              sx={{ bgcolor: EMERALD, color: INK, '&:hover': { bgcolor: '#22988c' } }}
            >
              {c.next}
            </Button>
          </Stack>
        </>
      )}

        {atResult && (
          <Result
            c={c}
            plan={plan}
            answers={effective}
            docsPrefix={docsPrefix}
            onRestart={() => {
              setA(DEFAULT_ANSWERS);
              setStep(0);
            }}
          />
        )}
      </Box>
    </DocsShell>
  );
}

// ── pieces ───────────────────────────────────────────────────────────────

function Question({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      )}
      <Stack spacing={1} sx={{ mt: 1.5 }}>
        {children}
      </Stack>
    </Box>
  );
}

function Choice({
  selected,
  label,
  hint,
  compact,
  onClick,
}: {
  selected: boolean;
  label: string;
  hint?: string;
  compact?: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{
        px: 2,
        py: compact ? 1 : 1.5,
        borderRadius: 2,
        border: '2px solid',
        borderColor: selected ? EMERALD : 'divider',
        bgcolor: selected ? 'rgba(46,194,179,0.06)' : 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        transition: 'border-color .12s, background-color .12s',
        '&:hover': { borderColor: selected ? EMERALD : 'text.disabled' },
      }}
    >
      <CheckCircleIcon sx={{ fontSize: 20, color: selected ? EMERALD : 'transparent' }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={selected ? 700 : 500}>
          {label}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function Result({
  c,
  plan,
  answers,
  docsPrefix,
  onRestart,
}: {
  c: SetupCopy;
  plan: ReturnType<typeof planInstall>;
  answers: Answers;
  docsPrefix: string;
  onRestart: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = async (what: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // A blocked clipboard is not worth an error state; the text is on screen.
    }
  };

  const download = () => {
    if (!plan.configBody || !plan.configFileName) return;
    const url = URL.createObjectURL(new Blob([plan.configBody], { type: 'text/plain' }));
    const el = document.createElement('a');
    el.href = url;
    el.download = plan.configFileName.split('/').pop()!;
    el.click();
    URL.revokeObjectURL(url);
  };

  if (answers.hosting === 'managed') {
    return (
      <Paper sx={{ p: 3.5, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          {c.result.managedTitle}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {c.result.managedBody}
        </Typography>
        <DocLinks c={c} plan={plan} docsPrefix={docsPrefix} />
        <Button startIcon={<ReplayIcon />} onClick={onRestart} sx={{ mt: 2, color: 'text.secondary' }}>
          {c.startOver}
        </Button>
      </Paper>
    );
  }

  const s = plan.sizing;

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, border: '1px solid', borderColor: EMERALD }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          {c.result.title}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            mb: 2,
          }}
        >
          <Metric label={c.result.cpu} value={String(s.vcpu)} />
          <Metric label={c.result.ram} value={`${s.ramGb} GB`} />
          <Metric label={c.result.disk} value={`${s.diskGb} GB`} />
          <Metric label={c.result.objectStorage} value={`${s.objectStorageGb.toLocaleString()} GB`} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={`${c.result.machine}: ${plan.instance ?? c.result.machineOwn}`}
            sx={{ fontWeight: 700, bgcolor: 'rgba(46,194,179,0.12)', color: '#1a8f83' }}
          />
          <Chip variant="outlined" label={`${c.result.peak}: ~${s.peakConcurrent.toLocaleString()}`} />
          {plan.replicas && (
            <Chip
              variant="outlined"
              label={`${c.result.replicas}: api ×${plan.replicas.api} · collector ×${plan.replicas.collector} · worker ×${plan.replicas.worker}`}
            />
          )}
        </Stack>
      </Paper>

      {plan.commands.length > 0 && (
        <Paper sx={{ p: 0, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {c.result.commands}
            </Typography>
            <Button
              size="small"
              startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
              onClick={() => copy('cmd', plan.commands.join('\n'))}
            >
              {copied === 'cmd' ? c.result.copied : c.result.copy}
            </Button>
          </Stack>
          <Box
            component="pre"
            sx={{
              m: 0,
              px: 2.5,
              py: 2,
              bgcolor: INK,
              color: '#dbe4e3',
              fontSize: 13,
              lineHeight: 1.7,
              overflowX: 'auto',
            }}
          >
            {plan.commands.join('\n')}
          </Box>
        </Paper>
      )}

      {plan.configBody && (
        <Paper sx={{ p: 0, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5, gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: 0 }}>
              {c.result.config}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {plan.configFileName}
              </Typography>
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Button
                size="small"
                startIcon={<ContentCopyIcon sx={{ fontSize: 15 }} />}
                onClick={() => copy('cfg', plan.configBody!)}
              >
                {copied === 'cfg' ? c.result.copied : c.result.copy}
              </Button>
              <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} onClick={download}>
                {c.result.download}
              </Button>
            </Stack>
          </Stack>
          <Box
            component="pre"
            sx={{
              m: 0,
              px: 2.5,
              py: 2,
              bgcolor: INK,
              color: '#dbe4e3',
              fontSize: 13,
              lineHeight: 1.7,
              overflowX: 'auto',
            }}
          >
            {plan.configBody}
          </Box>
        </Paper>
      )}

      {plan.warnings.length > 0 && (
        <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
            {c.result.watchOut}
          </Typography>
          <Stack spacing={1.25}>
            {plan.warnings.map((w) => (
              <Alert key={w} severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                <Typography variant="body2">{c.warnings[w]}</Typography>
              </Alert>
            ))}
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'divider' }}>
        <DocLinks c={c} plan={plan} docsPrefix={docsPrefix} />
      </Paper>

      <Box>
        <Button startIcon={<ReplayIcon />} onClick={onRestart} sx={{ color: 'text.secondary' }}>
          {c.startOver}
        </Button>
      </Box>
    </Stack>
  );
}

function DocLinks({
  c,
  plan,
  docsPrefix,
}: {
  c: SetupCopy;
  plan: ReturnType<typeof planInstall>;
  docsPrefix: string;
}) {
  return (
    <>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        {c.result.readNext}
      </Typography>
      <Stack spacing={0.5}>
        {plan.docLinks.map((key) => {
          const l = c.docLinks[key];
          return (
            <Stack key={key} direction="row" spacing={1} alignItems="center">
              <MenuBookIcon sx={{ fontSize: 16, color: EMERALD }} />
              <Box
                component={RouterLink}
                to={`${docsPrefix}/${l.slug}#${l.anchor}`}
                sx={{ fontSize: 14, color: '#1a8f83', textDecoration: 'none', '&:hover': { color: EMERALD } }}
              >
                {l.label}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}
